import { useEffect, useRef, useState } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertTriangle,
  CheckIcon,
  ChevronDown,
  Info,
  Link2,
  Loader2,
  Unlink,
  X,
} from "lucide-react";
import Swal from "sweetalert2";
import * as z from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import ClientSearchSelect from "~/pages/payments/components/ClientSearchSelect";
import { ClientSuggestion } from "~/pages/payments/hooks/useClientSuggestions";
import { useProfileStore } from "~/store/profileStore";
import { formatCurrency } from "~/utils/helpers";
import { resolveGross } from "~/utils/iva";
import { shouldMarkAsPaid } from "~/utils/paymentVerification";

interface SalesRequest {
  id: string;
  total_amount: number;
  paid_amount: number;
  status: string;
  created_at: string;
  price_with_iva: number;
  is_exento?: boolean;
  paid: boolean;
  created_at_formatted?: string;
  sales_request_documents?: Array<{
    bsale_number: string | null;
    document_type: string | null;
  }>;
}

interface ExistingPayment {
  id: number;
  amount: number;
  payment_method: string;
  rail_movement_id: string | null;
  bank: string | null;
  created_at: string;
  sales_request?: string; // Added for multiple sales request support
}

interface ManualFactura {
  id: string;
  doc_number: string | null;
  doc_type: string | null;
  description: string | null;
  dues: number | null;
  paid: number | null;
  balance: number | null;
  payment_due_date: string | null;
  created_at: string;
}

interface BankTransaction {
  id: string;
  amount: number;
  description: string;
  transaction_date: string;
  sender_account_name: string;
  sender_account_holder_id: string;
  is_linked?: boolean;
}

interface AssignPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: BankTransaction;
  onPaymentAssigned: (salesRequestId?: string, amount?: number) => void;
}

// Interface for individual sales request amounts
interface SalesRequestAmount {
  salesRequestId: string;
  amount: number;
}

// Create form schema - will be created dynamically based on selected sales requests
const createFormSchema = (
  selectedSalesRequests?: SalesRequest[],
  availableAmount?: number
) => {
  return z
    .object({
      clientId: z.string({
        required_error: "Por favor seleccione un cliente",
      }),
      assignmentType: z.enum(["sales_request", "manual_factura"], {
        required_error: "Por favor seleccione el tipo de asignación",
      }),
      operationType: z
        .enum(["create_new", "assign_existing"], {
          required_error: "Por favor seleccione el tipo de operación",
        })
        .optional(),
      amount: z.number().min(0.01, "El monto debe ser mayor a 0").optional(),
      salesRequestIds: z.array(z.string()).optional(),
      salesRequestAmounts: z
        .array(
          z.object({
            salesRequestId: z.string(),
            amount: z.number().min(0, "El monto no puede ser negativo"),
          })
        )
        .optional(),
      manualFacturaId: z.string().optional(),
      selectedPaymentId: z.number().optional(),
      selectedPaymentIds: z.array(z.number()).optional(),
    })
    .refine(
      (data) => {
        if (
          data.assignmentType === "sales_request" &&
          (!data.salesRequestIds || data.salesRequestIds.length === 0)
        ) {
          return false;
        }
        if (data.assignmentType === "manual_factura" && !data.manualFacturaId) {
          return false;
        }
        return true;
      },
      {
        message: "Por favor seleccione al menos un documento",
        path: ["salesRequestIds"],
      }
    )
    .refine(
      (data) => {
        // For assign_existing operation, payment selection is required
        if (
          data.assignmentType === "sales_request" &&
          data.operationType === "assign_existing"
        ) {
          // For single sales request, require selectedPaymentId
          if (data.salesRequestIds?.length === 1 && !data.selectedPaymentId) {
            return false;
          }
          // For multiple sales requests, require selectedPaymentIds with at least one selection
          if (
            data.salesRequestIds?.length > 1 &&
            (!data.selectedPaymentIds || data.selectedPaymentIds.length === 0)
          ) {
            return false;
          }
        }
        return true;
      },
      {
        message:
          "Por favor seleccione al menos un pago existente para vincular",
        path: ["selectedPaymentId"],
      }
    )
    .refine(
      (data) => {
        // For multiple payments, validate that total doesn't exceed movement amount
        if (
          data.assignmentType === "sales_request" &&
          data.operationType === "assign_existing" &&
          data.salesRequestIds?.length > 1 &&
          data.selectedPaymentIds?.length > 0 &&
          availableAmount
        ) {
          // Calculate total of selected payments - we'll need to access existingPayments somehow
          // This validation will be enhanced in the component logic
          return true; // For now, allow and validate in component
        }
        return true;
      },
      {
        message:
          "El total de pagos seleccionados no puede exceder el monto del movimiento",
        path: ["selectedPaymentIds"],
      }
    )
    .refine(
      (data) => {
        // For sales requests, validate that total assigned amount doesn't exceed available amount
        if (
          data.assignmentType === "sales_request" &&
          data.salesRequestAmounts &&
          availableAmount
        ) {
          const totalAssigned = data.salesRequestAmounts.reduce(
            (sum, item) => sum + item.amount,
            0
          );
          return totalAssigned <= availableAmount;
        }
        return true;
      },
      {
        message:
          "El total asignado no puede exceder el monto disponible de la transacción",
        path: ["salesRequestAmounts"],
      }
    )
    .refine(
      (data) => {
        // For sales requests, validate individual amounts don't exceed pending amounts
        if (
          data.assignmentType === "sales_request" &&
          data.salesRequestAmounts &&
          selectedSalesRequests
        ) {
          for (const amountItem of data.salesRequestAmounts) {
            const request = selectedSalesRequests.find(
              (r) => r.id === amountItem.salesRequestId
            );
            if (request) {
              const pendingAmount =
                resolveGross(request) - Math.round(request.paid_amount || 0);
              // Allow 0 for fully paid requests, otherwise enforce minimum and maximum
              if (pendingAmount > 0 && amountItem.amount > pendingAmount) {
                return false;
              }
            }
          }
        }
        return true;
      },
      {
        message: "Algunos montos exceden el saldo pendiente de las solicitudes",
        path: ["salesRequestAmounts"],
      }
    );
};

// Default form schema for initial setup
const formSchema = createFormSchema();

type FormData = z.infer<typeof formSchema>;

// Function to get display name for a status in Spanish
const getStatusDisplayName = (status: string): string => {
  switch (status) {
    case "accepted":
      return "Aprobada";
    case "pending":
      return "Pendiente";
    case "prepaid":
      return "Esperando Pago";
    case "payment_to_check":
      return "Verificar Pago";
    case "rejected":
      return "Rechazada";
    case "in_transit":
      return "En Camino";
    case "delivered":
      return "Entregado";
    case "test":
      return "Test";
    default:
      return (
        status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
      );
  }
};

// Function to get document type display name
const getDocTypeDisplayName = (docType: string | null): string => {
  switch (docType) {
    case "FV":
      return "Factura de Venta";
    case "PL":
      return "Pagare";
    case "NC":
      return "Nota de Crédito";
    case "ND":
      return "Nota de Débito";
    default:
      return docType || "N/A";
  }
};

export function AssignPaymentDialog({
  open,
  onOpenChange,
  transaction,
  onPaymentAssigned,
}: AssignPaymentDialogProps) {
  const { id: currentUserId } = useProfileStore();
  const [suggestedClients, setSuggestedClients] = useState<ClientSuggestion[]>(
    []
  );
  const [salesRequests, setSalesRequests] = useState<SalesRequest[]>([]);
  const [manualFacturas, setManualFacturas] = useState<ManualFactura[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedSalesRequests, setSelectedSalesRequests] = useState<
    SalesRequest[]
  >([]);
  const [salesRequestAmounts, setSalesRequestAmounts] = useState<
    SalesRequestAmount[]
  >([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isSubmittingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingSalesRequests, setLoadingSalesRequests] = useState(false);
  const [loadingManualFacturas, setLoadingManualFacturas] = useState(false);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [linkedPaymentInfo, setLinkedPaymentInfo] = useState<{
    clientName: string;
    salesRequestId?: string;
    manualFacturaId?: string;
    salesRequestTotal?: number;
    manualFacturaBalance?: number;
    docNumber?: string;
    type: "sales_request" | "manual_factura";
  } | null>(null);
  const [existingPayments, setExistingPayments] = useState<ExistingPayment[]>(
    []
  );
  const [hasMultiplePayments, setHasMultiplePayments] = useState(false);
  const [showOperationSelection, setShowOperationSelection] = useState(false);

  const { toast } = useToast();

  // Initialize form with dynamic schema
  const [currentSchema, setCurrentSchema] = useState(formSchema);
  const form = useForm<FormData>({
    resolver: zodResolver(currentSchema),
    defaultValues: {
      clientId: "",
      assignmentType: "sales_request",
      operationType: "create_new",
      amount: 0,
      salesRequestIds: [],
      salesRequestAmounts: [],
      manualFacturaId: "",
      selectedPaymentId: undefined,
      selectedPaymentIds: [],
    },
  });

  const clientId = form.watch("clientId");
  const assignmentType = form.watch("assignmentType");
  const operationType = form.watch("operationType");
  const amount = form.watch("amount");
  const salesRequestIds = form.watch("salesRequestIds");
  const manualFacturaId = form.watch("manualFacturaId");
  const selectedPaymentId = form.watch("selectedPaymentId");
  const selectedPaymentIds = form.watch("selectedPaymentIds");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update form resolver when schema changes
  useEffect(() => {
    // Re-create form with new schema while preserving values
    const currentValues = form.getValues();
    form.reset(currentValues);
    // Clear errors since validation rules may have changed
    form.clearErrors();
  }, [currentSchema, form]);

  // Check if transaction is linked and get linked payment info
  useEffect(() => {
    const checkLinkedStatus = async () => {
      if (!transaction || !open) return;

      try {
        // First try to get all payments for this movement with manual_factura_id
        let allPayments: any[] = [];
        let paymentsError: any = null;

        // Try the full query first
        const { data: paymentsWithManualFactura, error: fullQueryError } =
          await supabase
            .from("payments")
            .select(
              `
            id,
            amount,
            sales_request,
            manual_factura_id,
            client,
            clients!inner(name),
            sales_request(id, total_amount),
            manual_facturas(id, balance, doc_number)
          `
            )
            .eq("rail_movement_id", transaction.id);

        if (
          fullQueryError &&
          fullQueryError.message.includes("manual_factura_id")
        ) {
          // Fallback query without manual_factura_id if column doesn't exist
          const { data: paymentsWithoutManualFactura, error: fallbackError } =
            await supabase
              .from("payments")
              .select(
                `
              id,
              amount,
              sales_request,
              client,
              clients!inner(name),
              sales_request(id, total_amount)
            `
              )
              .eq("rail_movement_id", transaction.id);

          if (fallbackError) {
            paymentsError = fallbackError;
          } else {
            allPayments = paymentsWithoutManualFactura || [];
          }
        } else if (fullQueryError) {
          paymentsError = fullQueryError;
        } else {
          allPayments = paymentsWithManualFactura || [];
        }

        if (
          paymentsError &&
          !paymentsError.message.includes("manual_factura_id")
        ) {
          console.error("Error checking linked status:", paymentsError);
        }

        // Calculate total assigned amount
        const totalAssigned = allPayments.reduce(
          (sum, payment) => sum + (payment.amount || 0),
          0
        );
        const remainingBalance = Math.max(
          0,
          transaction.amount - totalAssigned
        );

        // Only show read-only view if movement is FULLY assigned (no remaining balance)
        // If there's remaining balance, always show assignment form regardless of existing payments
        if (remainingBalance <= 0 && allPayments.length > 0) {
          // Show the first payment as the "linked" payment for compatibility
          const firstPayment = allPayments[0];
          if (firstPayment.sales_request) {
            setLinkedPaymentInfo({
              clientName: firstPayment.clients.name,
              salesRequestId: firstPayment.sales_request.id,
              salesRequestTotal: firstPayment.sales_request.total_amount,
              type: "sales_request",
            });
          } else if (firstPayment.manual_facturas) {
            setLinkedPaymentInfo({
              clientName: firstPayment.clients.name,
              manualFacturaId: firstPayment.manual_facturas.id,
              manualFacturaBalance: firstPayment.manual_facturas.balance,
              docNumber: firstPayment.manual_facturas.doc_number,
              type: "manual_factura",
            });
          }
        } else {
          // Show assignment form - either no payments exist OR there's remaining balance
          setLinkedPaymentInfo(null);
        }
      } catch (err) {
        console.error("Error checking linked status:", err);
      }
    };

    checkLinkedStatus();
  }, [transaction, open]);

  // Load suggested clients based on transaction data
  useEffect(() => {
    const fetchSuggestedClients = async () => {
      if (!transaction || linkedPaymentInfo) return;

      setLoadingClients(true);
      try {
        // Try to find clients matching the transaction info
        let query = supabase
          .from("clients")
          .select("id, name, tax_id")
          .limit(5);

        // If we have sender information, search by tax_id and/or name
        if (
          transaction.sender_account_holder_id ||
          transaction.sender_account_name
        ) {
          const filters = [];

          // Add tax_id filter if we have sender ID
          if (transaction.sender_account_holder_id) {
            filters.push(
              `tax_id.ilike.%${transaction.sender_account_holder_id}%`
            );
          }

          // Add name filter if we have sender name
          if (transaction.sender_account_name) {
            filters.push(`name.ilike.%${transaction.sender_account_name}%`);
          }

          // Combine filters with OR
          query = query.or(filters.join(","));
        }

        const { data, error } = await query;

        if (error) throw error;

        const formattedClients: ClientSuggestion[] = (data || []).map(
          (client) => ({
            id: client.id,
            name: client.name,
            taxId: client.tax_id,
          })
        );

        setSuggestedClients(formattedClients);
      } catch (err) {
        console.error("Error fetching suggested clients:", err);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchSuggestedClients();
  }, [transaction, linkedPaymentInfo]);

  // Watch clientId changes to load sales requests and manual facturas
  useEffect(() => {
    if (clientId && clientId !== selectedClientId && !linkedPaymentInfo) {
      setSelectedClientId(clientId);
      if (assignmentType === "sales_request") {
        fetchSalesRequests(clientId);
      } else if (assignmentType === "manual_factura") {
        fetchManualFacturas(clientId);
      }
    }
  }, [clientId, assignmentType, selectedClientId, linkedPaymentInfo]);

  // Watch assignment type changes to load appropriate data
  useEffect(() => {
    if (selectedClientId && !linkedPaymentInfo) {
      if (assignmentType === "sales_request") {
        fetchSalesRequests(selectedClientId);
      } else if (assignmentType === "manual_factura") {
        fetchManualFacturas(selectedClientId);
        setSelectedSalesRequests([]); // Clear when switching to manual factura
        setSalesRequestAmounts([]);
      }
    }
  }, [assignmentType, selectedClientId, linkedPaymentInfo]);

  // Add effect to reset the form when dialog closes
  useEffect(() => {
    if (!open) {
      // Wait a bit before resetting to avoid UI flicker
      const timeout = setTimeout(() => {
        form.reset();
        setSelectedClientId(null);
        setSelectedSalesRequests([]);
        setSalesRequestAmounts([]);
        setSalesRequests([]);
        setManualFacturas([]);
        setLinkedPaymentInfo(null);
        setExistingPayments([]);
        setHasMultiplePayments(false);
        setIsDropdownOpen(false);
        setShowOperationSelection(false);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [open, form]);

  // Load sales requests for a client
  const fetchSalesRequests = async (clientId: string) => {
    setLoadingSalesRequests(true);
    try {
      const { data, error } = await supabase
        .from("sales_request")
        .select(
          `id, total_amount, paid_amount, status, created_at, price_with_iva, is_exento, paid,
           sales_request_documents!sales_request_id(bsale_number, document_type)`
        )
        .eq("client_id", clientId)
        .in("status", [
          "pending",
          "prepaid",
          "accepted",
          "por_facturar",
          "in_transit",
          "delivered",
        ]) // Include pending, prepaid, accepted, por_facturar, in_transit and delivered requests
        .order("created_at", { ascending: false });

      if (error) throw error;

      // The cached sales_request.paid_amount / paid columns can drift out of
      // sync (e.g. payments deleted/unlinked without a recompute), producing a
      // phantom "Pagado" in this dialog. Always read the authoritative paid
      // amount from the RPC instead of trusting the cached column.
      const paidByRequest = new Map<string, number>();
      const requestIds = (data || []).map((request) => request.id);
      if (requestIds.length > 0) {
        const { data: paidRows, error: paidError } = await supabase.rpc(
          "get_bulk_sales_request_paid_amounts",
          { sales_request_ids: requestIds }
        );
        if (paidError) throw paidError;
        (paidRows || []).forEach((row) => {
          paidByRequest.set(row.sales_request_id, Number(row.paid_amount) || 0);
        });
      }

      // Format date to be more user-friendly in the display
      const formattedRequests = (data || []).map((request) => {
        // The RPC only returns a row for requests that actually have payments.
        // A missing entry therefore means zero verified payments, NOT "unknown"
        // — fall back to 0, never to the cached paid_amount column, which can be
        // drifted/stale and would wrongly mark an unpaid request as paid (and
        // hide its factura from this dialog).
        const paidAmount = paidByRequest.has(request.id)
          ? (paidByRequest.get(request.id) as number)
          : 0;
        return {
          ...request,
          paid_amount: paidAmount,
          paid: paidAmount >= resolveGross(request),
          created_at_formatted: new Date(request.created_at).toLocaleDateString(
            "es-ES",
            {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }
          ),
        };
      });

      setSalesRequests(formattedRequests);

      // Clear the sales request selection if we're changing clients
      form.setValue("salesRequestIds", []);
      form.setValue("salesRequestAmounts", []);
      form.setValue("selectedPaymentId", undefined);
      form.setValue("selectedPaymentIds", []);
      setSelectedSalesRequests([]);
      setSalesRequestAmounts([]);
      setExistingPayments([]);
      setHasMultiplePayments(false);
    } catch (err) {
      console.error("Error fetching sales requests:", err);
      toast({
        title: "Error",
        description: "No se pudieron cargar las solicitudes de venta",
        variant: "destructive",
      });
    } finally {
      setLoadingSalesRequests(false);
    }
  };

  // Load existing payments for a sales request
  const fetchExistingPayments = async (salesRequestId: string) => {
    try {
      const { data, error } = await supabase
        .from("payments")
        .select(
          "id, amount, payment_method, rail_movement_id, bank, created_at"
        )
        .eq("sales_request", salesRequestId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const payments = data || [];
      setExistingPayments(payments);
      // Clear any existing payment selections when fetching new payments
      form.setValue("selectedPaymentId", undefined);
      form.setValue("selectedPaymentIds", []);

      // Check if there are multiple unlinked payments
      const unlinkedPayments = payments.filter((p) => !p.rail_movement_id);
      setHasMultiplePayments(unlinkedPayments.length > 1);

      // Show operation selection if there are existing payments
      if (unlinkedPayments.length > 0) {
        setShowOperationSelection(true);
        // Default to assign existing if there are payments
        form.setValue("operationType", "assign_existing");
      } else {
        setShowOperationSelection(true);
        // Default to create new if no existing payments
        form.setValue("operationType", "create_new");
      }

      // Auto-select if there's an exact amount match
      if (unlinkedPayments.length > 1 && transaction) {
        const exactMatch = unlinkedPayments.find(
          (p) => p.amount === transaction.amount
        );
        if (exactMatch) {
          form.setValue("selectedPaymentId", exactMatch.id);
        }
      }
    } catch (err) {
      console.error("Error fetching existing payments:", err);
    }
  };

  // Load existing payments for multiple sales requests
  const fetchExistingPaymentsForMultiple = async (
    salesRequestIds: string[]
  ) => {
    try {
      const { data, error } = await supabase
        .from("payments")
        .select(
          "id, amount, payment_method, rail_movement_id, bank, created_at, sales_request"
        )
        .in("sales_request", salesRequestIds)
        .order("created_at", { ascending: true });
      if (error) throw error;
      const payments = data || [];
      setExistingPayments(payments);
      // Clear any existing payment selections when fetching new payments
      form.setValue("selectedPaymentId", undefined);
      form.setValue("selectedPaymentIds", []);
      // Check if there are multiple unlinked payments across all sales requests
      const unlinkedPayments = payments.filter((p) => !p.rail_movement_id);
      setHasMultiplePayments(unlinkedPayments.length > 1);
      // Show operation selection if there are existing payments
      if (unlinkedPayments.length > 0) {
        setShowOperationSelection(true);
        // Default to assign existing if there are payments
        form.setValue("operationType", "assign_existing");
      } else {
        setShowOperationSelection(true);
        // Default to create new if no existing payments
        form.setValue("operationType", "create_new");
      }
    } catch (err) {
      console.error(
        "Error fetching existing payments for multiple requests:",
        err
      );
    }
  };

  // Load manual facturas for a client
  const fetchManualFacturas = async (clientId: string) => {
    setLoadingManualFacturas(true);
    try {
      const { data, error } = await supabase
        .from("manual_facturas")
        .select(
          "id, doc_number, doc_type, description, dues, paid, balance, payment_due_date, created_at"
        )
        .eq("client_id", clientId)
        .gt("balance", 0) // Only show facturas with pending balance
        .order("created_at", { ascending: false });

      if (error) throw error;

      setManualFacturas(data || []);

      // Clear the manual factura selection if we're changing clients
      form.setValue("manualFacturaId", "");
      // Also clear sales request selections when switching clients
      form.setValue("salesRequestIds", []);
      form.setValue("salesRequestAmounts", []);
      setSelectedSalesRequests([]);
      setSalesRequestAmounts([]);
    } catch (err) {
      console.error("Error fetching manual facturas:", err);
      toast({
        title: "Error",
        description: "No se pudieron cargar las facturas manuales",
        variant: "destructive",
      });
    } finally {
      setLoadingManualFacturas(false);
    }
  };

  // Handle form submission for linking
  const onSubmit = async (data: FormData) => {
    // Prevent double submissions with synchronous ref check
    if (isSubmittingRef.current) {
      console.log("Submission already in progress, ignoring duplicate click");
      return;
    }
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      // Validate fresh remaining balance before proceeding
      if (transaction?.id) {
        const { data: existingPaymentsForMovement } = await supabase
          .from("payments")
          .select("amount")
          .eq("rail_movement_id", transaction.id);

        const currentTotalAssigned =
          existingPaymentsForMovement?.reduce(
            (sum, p) => sum + Number(p.amount),
            0
          ) || 0;
        const freshRemainingBalance = transaction.amount - currentTotalAssigned;

        // Calculate total amount being requested in this submission
        let totalRequestedAmount = 0;
        if (
          data.assignmentType === "sales_request" &&
          data.salesRequestAmounts
        ) {
          totalRequestedAmount = data.salesRequestAmounts.reduce(
            (sum, item) => sum + (item.amount || 0),
            0
          );
        } else if (data.assignmentType === "manual_factura" && data.amount) {
          totalRequestedAmount = data.amount;
        }

        if (totalRequestedAmount > freshRemainingBalance) {
          toast({
            title: "Error",
            description: `El monto solicitado ($${formatCurrency(totalRequestedAmount)}) excede el saldo disponible del movimiento ($${formatCurrency(freshRemainingBalance)})`,
            variant: "destructive",
          });
          return;
        }
      }

      if (
        data.assignmentType === "sales_request" &&
        data.salesRequestIds &&
        data.salesRequestIds.length > 0
      ) {
        // Handle multiple sales request payments with direct database operations

        // Get bank information from the transaction using proper table relationships (once for all)
        const { data: movementData } = await supabase
          .from("rail_movements")
          .select(
            `
            account_id,
            rail_bank_accounts!inner(
              rail_bank_id,
              rail_banks!inner(institution_name)
            )
          `
          )
          .eq("id", transaction.id)
          .single();

        // Extract bank name from proper table joins
        const bankName =
          movementData?.rail_bank_accounts?.rail_banks?.institution_name ||
          null;

        let totalAssignedAmount = 0;
        let assignedRequestsCount = 0;
        let splitPaymentsCount = 0;
        const processedRequests: string[] = [];

        // Handle multiple payment assignment vs individual sales request processing
        if (
          data.operationType === "assign_existing" &&
          data.salesRequestIds.length > 1
        ) {
          // Multiple sales requests with existing payments - process all selected payments together

          // Get selected payments (already validated above)
          const selectedPayments =
            existingPayments?.filter((p) =>
              data.selectedPaymentIds?.includes(p.id)
            ) || [];

          // Assign same Fintoc movement ID to all selected payments
          for (const payment of selectedPayments) {
            const { error: paymentUpdateError } = await supabase
              .from("payments")
              .update({
                rail_movement_id: transaction.id,
                bank: bankName,
                is_verified: true,
                verified_by: currentUserId,
                verified_at: new Date().toISOString(),
              })
              .eq("id", payment.id);

            if (paymentUpdateError) {
              console.error(
                `Error updating payment ${payment.id}:`,
                paymentUpdateError
              );
              throw new Error(
                `Error al actualizar el pago con ID ${payment.id}`
              );
            }

            totalAssignedAmount += payment.amount;
          }

          // Update paid status for each affected sales request
          const affectedSalesRequests = [
            ...new Set(
              selectedPayments.map((p) => p.sales_request).filter(Boolean)
            ),
          ];

          for (const salesRequestId of affectedSalesRequests) {
            // Calculate if this sales request should be marked as paid
            // (exempt-aware: facturas exentas SII 34 carry no IVA -> gross = net)
            const affectedRequest = selectedSalesRequests.find(
              (r) => r.id === salesRequestId
            );
            const totalAmount = affectedRequest
              ? resolveGross(affectedRequest)
              : 0;
            const isPaidInFull = await shouldMarkAsPaid(
              salesRequestId,
              totalAmount
            );

            const { error: updateError } = await supabase
              .from("sales_request")
              .update({
                paid: isPaidInFull,
                updated_at: new Date().toISOString(),
              })
              .eq("id", salesRequestId);

            if (updateError) {
              console.error(
                `Error updating sales request ${salesRequestId}:`,
                updateError
              );
              throw new Error(
                `Error al actualizar solicitud de venta ${salesRequestId}`
              );
            }
          }

          assignedRequestsCount = affectedSalesRequests.length;
        } else {
          // Single sales request or create new payment mode - use existing logic
          for (const salesRequestId of data.salesRequestIds) {
            const requestAmount =
              data.salesRequestAmounts?.find(
                (a) => a.salesRequestId === salesRequestId
              )?.amount || 0;

            if (requestAmount < 0) {
              throw new Error(
                `Monto inválido para la solicitud ${salesRequestId}`
              );
            }

            try {
              // Get the current sales request data
              const { data: salesRequestData, error: fetchError } =
                await supabase
                  .from("sales_request")
                  .select(
                    "paid_amount, total_amount, price_with_iva, is_exento, status, paid, sales_condition_id"
                  )
                  .eq("id", salesRequestId)
                  .single();

              if (fetchError) {
                console.error(
                  `Error fetching sales request ${salesRequestId}:`,
                  fetchError
                );
                throw new Error(
                  `Error al obtener datos de la solicitud de venta ${salesRequestId}`
                );
              }

              // Check if there are existing payments for this sales request
              const { data: existingPayments, error: paymentFetchError } =
                await supabase
                  .from("payments")
                  .select(
                    "id, amount, rail_movement_id, payment_method, bank, created_at"
                  )
                  .eq("sales_request", salesRequestId);

              if (paymentFetchError) {
                console.error(
                  `Error fetching existing payments for ${salesRequestId}:`,
                  paymentFetchError
                );
                throw new Error(
                  `Error al verificar pagos existentes para ${salesRequestId}`
                );
              }

              // Handle operation based on user's choice
              if (data.operationType === "assign_existing") {
                // Assign existing payment mode - handle single or multiple payments
                let selectedPayments = [];

                if (data.salesRequestIds.length === 1) {
                  // Single sales request - use selectedPaymentId
                  let existingPayment = null;
                  if (data.selectedPaymentId) {
                    existingPayment = existingPayments?.find(
                      (p) => p.id === data.selectedPaymentId
                    );
                    if (!existingPayment) {
                      throw new Error(
                        `No se encontró el pago seleccionado con ID ${data.selectedPaymentId}`
                      );
                    }
                  } else if (
                    existingPayments &&
                    existingPayments.length === 1
                  ) {
                    existingPayment = existingPayments[0];
                  } else {
                    throw new Error(
                      `Por favor seleccione el pago que debe ser vinculado al movimiento de Fintoc.`
                    );
                  }
                  selectedPayments = [existingPayment];
                } else {
                  // Multiple sales requests - use selectedPaymentIds
                  if (
                    data.selectedPaymentIds &&
                    data.selectedPaymentIds.length > 0
                  ) {
                    selectedPayments =
                      existingPayments?.filter((p) =>
                        data.selectedPaymentIds.includes(p.id)
                      ) || [];

                    if (
                      selectedPayments.length !== data.selectedPaymentIds.length
                    ) {
                      const missingIds = data.selectedPaymentIds.filter(
                        (id) => !existingPayments?.some((p) => p.id === id)
                      );
                      console.error("Missing payment IDs:", missingIds);
                      throw new Error(
                        `No se encontraron los pagos con IDs: ${missingIds.join(", ")}`
                      );
                    }
                  } else {
                    throw new Error(
                      `Por favor seleccione los pagos que deben ser vinculados al movimiento de Fintoc.`
                    );
                  }
                }

                // Process all selected payments with support for payment splitting

                for (const payment of selectedPayments) {
                  // Handle payment splitting if needed (when payment amount > transaction amount)
                  if (
                    payment.amount > transaction.amount &&
                    selectedPayments.length === 1
                  ) {
                    // Split payment: existing payment is larger than movement

                    // 1. Reduce original payment amount (keep unverified)
                    const { error: reduceError } = await supabase
                      .from("payments")
                      .update({
                        amount: payment.amount - transaction.amount,
                      })
                      .eq("id", payment.id);

                    if (reduceError) {
                      console.error(
                        `Error reducing original payment for ${salesRequestId}:`,
                        reduceError
                      );
                      throw new Error(
                        `Error al reducir el pago original para ${salesRequestId}`
                      );
                    }

                    // 2. Create new verified payment for the movement amount
                    const splitPaymentData = {
                      amount: Math.round(transaction.amount),
                      client: payment.client,
                      sales_request: salesRequestId,
                      manual_factura_id: payment.manual_factura_id || null,
                      payment_method: "bank_transfer",
                      rail_movement_id: transaction.id,
                      bank: bankName,
                      is_verified: true,
                      verified_by: currentUserId,
                      verified_at: new Date().toISOString(),
                      created_by: currentUserId,
                      payment_status: "completed",
                    };

                    // Validation
                    if (
                      !splitPaymentData.amount ||
                      splitPaymentData.amount <= 0
                    ) {
                      throw new Error(
                        "Invalid payment amount for split payment"
                      );
                    }
                    if (!splitPaymentData.sales_request) {
                      throw new Error(
                        "Missing sales request ID for split payment"
                      );
                    }

                    // Get client ID from sales request if not available in payment
                    if (!splitPaymentData.client) {
                      const {
                        data: salesRequestData,
                        error: salesRequestError,
                      } = await supabase
                        .from("sales_request")
                        .select("client_id")
                        .eq("id", salesRequestId)
                        .single();

                      if (salesRequestError || !salesRequestData?.client_id) {
                        throw new Error(
                          "Could not determine client ID for split payment"
                        );
                      }

                      splitPaymentData.client = salesRequestData.client_id;
                    }

                    const { data: newPayment, error: createError } =
                      await supabase
                        .from("payments")
                        .insert(splitPaymentData)
                        .select();

                    if (createError) {
                      console.error(
                        `Error creating split payment for ${salesRequestId}:`,
                        createError
                      );
                      throw new Error(
                        `Error al crear el pago dividido para ${salesRequestId}: ${createError.message}`
                      );
                    }

                    // Verify the payment was actually created by querying it back
                    if (newPayment && newPayment.length > 0) {
                      const createdPaymentId = newPayment[0].id;
                      const { data: verifyPayment, error: verifyError } =
                        await supabase
                          .from("payments")
                          .select("*")
                          .eq("id", createdPaymentId)
                          .single();

                      if (verifyError || !verifyPayment) {
                        console.error(
                          "Failed to verify created payment:",
                          verifyError
                        );
                        throw new Error("Payment creation verification failed");
                      } else {
                      }
                    } else {
                      console.error("No payment data returned from insert");
                      throw new Error("Payment insert returned no data");
                    }

                    // Use the movement amount for calculation
                    totalAssignedAmount += transaction.amount;
                    splitPaymentsCount++;
                  } else if (payment.amount <= transaction.amount) {
                    // Normal case: payment amount <= transaction amount
                    // Simply assign the same Fintoc movement ID to the payment
                    const { error: paymentUpdateError } = await supabase
                      .from("payments")
                      .update({
                        rail_movement_id: transaction.id,
                        bank: bankName,
                        // Assigning a Fintoc movement verifies the payment
                        is_verified: true,
                        verified_by: currentUserId,
                        verified_at: new Date().toISOString(),
                      })
                      .eq("id", payment.id);

                    if (paymentUpdateError) {
                      console.error(
                        `Error updating payment ${payment.id}:`,
                        paymentUpdateError
                      );
                      throw new Error(
                        `Error al actualizar el pago con ID ${payment.id}`
                      );
                    }

                    // Add to total assigned amount
                    totalAssignedAmount += payment.amount;
                  } else {
                    // Error case: multiple payments but one exceeds transaction amount
                    throw new Error(
                      `El movimiento de $${formatCurrency(transaction.amount)} no puede manejar múltiples pagos cuando uno excede el monto total. Pago: $${formatCurrency(payment.amount)} para ${salesRequestId}`
                    );
                  }
                }
              } else {
                // Create new payment mode - always create new payment
                if (requestAmount > 0) {
                  const { error: paymentError } = await supabase
                    .from("payments")
                    .insert({
                      amount: Math.round(requestAmount),
                      client: data.clientId,
                      sales_request: salesRequestId,
                      payment_method: "bank_transfer",
                      rail_movement_id: transaction.id,
                      bank: bankName,
                      // New Fintoc payments are automatically verified
                      is_verified: true,
                      verified_at: new Date().toISOString(),
                    });

                  if (paymentError) {
                    console.error(
                      `Error creating payment for ${salesRequestId}:`,
                      paymentError
                    );
                    throw new Error(
                      `Error al crear el registro de pago para ${salesRequestId}`
                    );
                  }

                  totalAssignedAmount += requestAmount;
                }
              }

              // Calculate new paid amount using RPC function that excludes checks in portfolio
              const { data: newPaidAmount, error: paidAmountError } =
                await supabase.rpc("get_sales_request_paid_amount", {
                  sales_request_id: salesRequestId,
                });

              if (paidAmountError) {
                console.error(
                  `Error calculating paid amount for ${salesRequestId}:`,
                  paidAmountError
                );
                throw new Error(
                  `Error al calcular el monto pagado para ${salesRequestId}`
                );
              }

              // Calculate if sales request should be marked as paid based on verified payments
              // (exempt-aware: facturas exentas SII 34 carry no IVA -> gross = net)
              const totalAmount = resolveGross(salesRequestData);

              // Use verification-based paid status calculation
              const isPaidInFull = await shouldMarkAsPaid(
                salesRequestId,
                totalAmount
              );

              const updateData: any = {
                paid_amount: newPaidAmount || 0,
                paid: isPaidInFull, // Only set to true if verified payments cover total
              };

              const { error: updateError } = await supabase
                .from("sales_request")
                .update(updateData)
                .eq("id", salesRequestId);

              if (updateError) {
                console.error(
                  `Error updating sales request ${salesRequestId}:`,
                  updateError
                );
                throw new Error(
                  `Error al actualizar la solicitud de venta ${salesRequestId}`
                );
              }

              // Force the correct paid status if the trigger overrode it
              // This is a workaround for the BEFORE UPDATE trigger issue
              if (isPaidInFull) {
                const { error: paidUpdateError } = await supabase
                  .from("sales_request")
                  .update({ paid: true })
                  .eq("id", salesRequestId)
                  .eq("paid", false); // Only update if it's still false

                if (paidUpdateError) {
                  console.error(
                    `Error forcing paid status for ${salesRequestId}:`,
                    paidUpdateError
                  );
                  // Don't throw error here as the main update succeeded
                }

                // Check if this is TRANSFERENCIA ANTICIPADA and update status to accepted
                if (salesRequestData.sales_condition_id) {
                  try {
                    const { data: salesCondition, error: conditionError } =
                      await supabase
                        .from("sales_conditions")
                        .select("name")
                        .eq("id", salesRequestData.sales_condition_id)
                        .single();

                    if (conditionError) {
                      console.error(
                        `Error fetching sales condition for ${salesRequestId}:`,
                        conditionError
                      );
                    } else if (
                      salesCondition?.name === "TRANSFERENCIA ANTICIPADA" &&
                      salesRequestData.status === "prepaid"
                    ) {
                      const { error: statusUpdateError } = await supabase
                        .from("sales_request")
                        .update({ status: "accepted" })
                        .eq("id", salesRequestId)
                        .eq("status", "prepaid"); // Only update if still prepaid

                      if (statusUpdateError) {
                        console.error(
                          `Error updating status to accepted for ${salesRequestId}:`,
                          statusUpdateError
                        );
                      } else {
                      }
                    }
                  } catch (error) {
                    console.error(
                      `Error processing TRANSFERENCIA ANTICIPADA status update for ${salesRequestId}:`,
                      error
                    );
                  }
                }
              }

              assignedRequestsCount++;
              processedRequests.push(salesRequestId);
            } catch (error) {
              console.error(
                `Error processing sales request ${salesRequestId}:`,
                error
              );
              // If we've processed some requests successfully, we need to handle partial failure
              if (processedRequests.length > 0) {
                throw new Error(
                  `Error parcial: Se procesaron ${processedRequests.length} de ${data.salesRequestIds.length} solicitudes. Error en ${salesRequestId}: ${error instanceof Error ? error.message : "Error desconocido"}`
                );
              }
              throw error;
            }
          }
        }

        // Create appropriate success message based on what happened
        let description = `Se han asignado $${formatCurrency(totalAssignedAmount)} a ${assignedRequestsCount} solicitud${assignedRequestsCount > 1 ? "es" : ""} de venta`;

        if (splitPaymentsCount > 0) {
          description += `. Se ${splitPaymentsCount === 1 ? "dividió" : "dividieron"} ${splitPaymentsCount} pago${splitPaymentsCount > 1 ? "s" : ""} para ajustar los montos`;
        }

        toast({
          title: "Transferencias asignadas",
          description,
        });

        onPaymentAssigned(data.salesRequestIds[0], totalAssignedAmount);
      } else if (
        data.assignmentType === "manual_factura" &&
        data.manualFacturaId
      ) {
        // Handle manual factura payment with direct database operations

        // First, get the current manual factura data
        const { data: facturaData, error: fetchError } = await supabase
          .from("manual_facturas")
          .select("paid, dues, balance")
          .eq("id", data.manualFacturaId)
          .single();

        if (fetchError) {
          console.error("Error fetching manual factura:", fetchError);
          throw new Error("Error al obtener datos de la factura manual");
        }

        // Get bank information from the transaction using proper table relationships
        const { data: movementData } = await supabase
          .from("rail_movements")
          .select(
            `
            account_id,
            rail_bank_accounts!inner(
              rail_bank_id,
              rail_banks!inner(institution_name)
            )
          `
          )
          .eq("id", transaction.id)
          .single();

        // Extract bank name from proper table joins
        const bankName =
          movementData?.rail_bank_accounts?.rail_banks?.institution_name ||
          null;

        // Create the payment record
        const { error: paymentError } = await supabase.from("payments").insert({
          amount: Math.round(data.amount),
          client: data.clientId,
          manual_factura_id: data.manualFacturaId,
          payment_method: "bank_transfer",
          rail_movement_id: transaction.id,
          bank: bankName,
          // New Fintoc payments are automatically verified
          is_verified: true,
          verified_at: new Date().toISOString(),
        });

        if (paymentError) {
          console.error("Error creating payment:", paymentError);
          throw new Error("Error al crear el registro de pago");
        }

        // Calculate new paid amount using RPC function that excludes checks in portfolio
        const { data: newPaidAmount, error: paidAmountError } =
          await supabase.rpc("get_manual_factura_paid_amount", {
            factura_id: data.manualFacturaId,
          });

        if (paidAmountError) {
          console.error("Error calculating paid amount:", paidAmountError);
          throw new Error("Error al calcular el monto pagado");
        }

        const duesAmount = facturaData.dues || 0;
        const newBalance = duesAmount - (newPaidAmount || 0);

        // Update the manual factura with new paid amount and balance
        const { error: updateError } = await supabase
          .from("manual_facturas")
          .update({
            paid: newPaidAmount || 0,
            balance: newBalance,
          })
          .eq("id", data.manualFacturaId);

        if (updateError) {
          console.error("Error updating manual factura:", updateError);
          throw new Error("Error al actualizar la factura manual");
        }

        toast({
          title: "Pago asignado",
          description:
            "El pago se ha asignado correctamente a la factura manual",
        });

        onPaymentAssigned(data.manualFacturaId, data.amount);
      } else {
        throw new Error(
          "No se pudo procesar la asignación: tipo de asignación no válido"
        );
      }
    } catch (err) {
      console.error("Error assigning payment:", err);
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "No se pudo asignar el pago",
        variant: "destructive",
      });
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  // Handle unlinking payment
  const handleUnlink = async () => {
    const unlinkFunction =
      linkedPaymentInfo?.type === "sales_request"
        ? "unlink_movement_from_payment"
        : "unlink_movement_from_manual_factura_payment";

    // Show confirmation modal
    const result = await Swal.fire({
      title: "¿Desvincular pago?",
      html: `
        <div class="text-left">
          <p class="mb-2">¿Está seguro que desea desvincular este pago?</p>
          <div class="bg-gray-50 p-3 rounded border">
            <p><strong>Monto:</strong> ${formatCurrency(transaction.amount)}</p>
            <p><strong>Remitente:</strong> ${transaction.sender_account_name || "Desconocido"}</p>
            <p><strong>RUT:</strong> ${transaction.sender_account_holder_id || "N/A"}</p>
            ${
              linkedPaymentInfo
                ? `
              <p><strong>Cliente:</strong> ${linkedPaymentInfo.clientName}</p>
              <p><strong>Documento:</strong> ${
                linkedPaymentInfo.type === "sales_request"
                  ? `Nota de Venta ${linkedPaymentInfo.salesRequestId?.substring(0, 8)}...`
                  : `Factura ${linkedPaymentInfo.docNumber || "sin número"}`
              }</p>
            `
                : ""
            }
          </div>
          <p class="mt-2 text-sm text-gray-600">Esta acción eliminará la vinculación del pago con el documento.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, desvincular",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      customClass: {
        container: "swal2-z-top",
        popup: "z-[100000]",
      },
      // Strong mobile isolation
      allowOutsideClick: false,
      allowEscapeKey: false,
      backdrop: `rgba(0,0,0,0.6)`,
      heightAuto: false,
      scrollbarPadding: false,
      stopKeydownPropagation: true,
      focusConfirm: true,
      buttonsStyling: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    setUnlinkLoading(true);
    try {
      // Call the appropriate RPC function to unlink the transaction from the payments
      const { data: result, error } = await supabase.rpc(unlinkFunction, {
        p_movement_id: transaction.id,
      });

      if (error) throw error;

      toast({
        title: "Pago desvinculado",
        description:
          "El pago se ha desvinculado correctamente de los documentos",
      });

      // Reset the linked payment info
      setLinkedPaymentInfo(null);

      // Notify parent component
      onPaymentAssigned();
    } catch (err) {
      console.error("Error unlinking payment:", err);
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "No se pudo desvincular el pago",
        variant: "destructive",
      });
    } finally {
      setUnlinkLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent
        className="flex max-h-[85vh] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
        style={{ zIndex: 9998 }}
      >
        <DialogHeader className="flex-shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle>
            {linkedPaymentInfo
              ? "Pago Vinculado"
              : "Asignar Pago a Cliente y Documento"}
          </DialogTitle>
          <DialogDescription>
            {linkedPaymentInfo
              ? "Este pago ya está vinculado a un cliente y documento."
              : "Vincule este pago a un cliente y a un documento existente."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {/* Transaction summary */}
          <div className="rounded-lg border bg-muted/40 p-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">
                  Monto total
                </p>
                <p className="text-sm font-semibold tabular-nums">
                  ${formatCurrency(transaction.amount)}
                </p>
              </div>
              {(transaction as any).total_assigned !== undefined && (
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground">
                    Ya asignado
                  </p>
                  <p
                    className={`text-sm font-semibold tabular-nums ${
                      (transaction as any).total_assigned > 0
                        ? "text-green-700"
                        : ""
                    }`}
                  >
                    ${formatCurrency((transaction as any).total_assigned)}
                  </p>
                </div>
              )}
              {(transaction as any).remaining_balance !== undefined && (
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground">
                    Disponible
                  </p>
                  <p className="text-sm font-semibold tabular-nums">
                    ${formatCurrency((transaction as any).remaining_balance)}
                  </p>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">
                  Remitente
                </p>
                <p className="truncate text-sm font-medium">
                  {transaction.sender_account_name || "Desconocido"}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {transaction.sender_account_holder_id || "RUT no disponible"}
                </p>
              </div>
            </div>
            {transaction.description && (
              <div className="mt-3 border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Descripción
                </p>
                <p className="text-sm font-medium">{transaction.description}</p>
              </div>
            )}
          </div>

          {linkedPaymentInfo ? (
            // Show linked payment information
            <div className="space-y-4 rounded-lg border p-4">
              <h3 className="text-sm font-semibold">
                Información del Pago Vinculado
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm font-medium">Cliente</p>
                  <p className="text-sm text-muted-foreground">
                    {linkedPaymentInfo.clientName}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Documento</p>
                  <p className="text-sm text-muted-foreground">
                    {linkedPaymentInfo.type === "sales_request" ? (
                      <>
                        Nota de Venta:{" "}
                        {linkedPaymentInfo.salesRequestId?.substring(0, 8)}... |
                        Total: $
                        {formatCurrency(
                          linkedPaymentInfo.salesRequestTotal || 0
                        )}
                      </>
                    ) : (
                      <>
                        Factura Manual:{" "}
                        {linkedPaymentInfo.docNumber || "sin número"} | Saldo: $
                        {formatCurrency(
                          linkedPaymentInfo.manualFacturaBalance || 0
                        )}
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Show assignment form
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-5"
              >
                <FormField
                  control={form.control}
                  name="clientId"
                  render={({ field }) => (
                    <FormItem
                      className="flex w-full min-w-0 flex-col gap-y-2"
                      style={{ position: "relative", zIndex: 20 }}
                    >
                      <FormLabel>Cliente</FormLabel>
                      <FormControl style={{ position: "relative", zIndex: 20 }}>
                        <ClientSearchSelect
                          value={field.value || ""}
                          onChange={field.onChange}
                          defaultSuggestions={suggestedClients}
                        />
                      </FormControl>
                      <FormDescription className="truncate">
                        Busque por RUT del cliente que realizó este pago
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="assignmentType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Documento</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="flex flex-wrap gap-x-6 gap-y-2"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="sales_request"
                              id="sales_request"
                            />
                            <Label htmlFor="sales_request">Nota de Venta</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="manual_factura"
                              id="manual_factura"
                            />
                            <Label htmlFor="manual_factura">
                              Factura Manual
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {assignmentType === "sales_request" && (
                  <FormField
                    control={form.control}
                    name="salesRequestIds"
                    render={({ field }) => (
                      <FormItem className="relative z-20 w-full min-w-0">
                        <FormLabel>Solicitudes de Venta</FormLabel>
                        <FormControl className="relative z-20">
                          {loadingSalesRequests ? (
                            <div className="flex items-center justify-center rounded-md border p-4">
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              <span>Cargando solicitudes...</span>
                            </div>
                          ) : (
                            <div ref={dropdownRef} className="relative">
                              {/* Trigger Button */}
                              <button
                                type="button"
                                onClick={() =>
                                  setIsDropdownOpen(!isDropdownOpen)
                                }
                                disabled={
                                  !selectedClientId ||
                                  loadingSalesRequests ||
                                  loading
                                }
                                className="flex min-h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <div className="mr-2 flex flex-1 flex-wrap gap-1">
                                  {(field.value || []).length === 0 ? (
                                    <span className="text-muted-foreground">
                                      Seleccionar solicitudes de venta
                                    </span>
                                  ) : (
                                    (field.value || []).map((id: string) => {
                                      const request = salesRequests.find(
                                        (r) => r.id === id
                                      );
                                      if (!request) return null;

                                      const facturaDoc =
                                        request.sales_request_documents?.find(
                                          (doc) =>
                                            doc.document_type === "factura" &&
                                            doc.bsale_number
                                        );
                                      const facturaNumber =
                                        facturaDoc?.bsale_number ||
                                        `ID: ${request.id.substring(0, 8)}...`;

                                      return (
                                        <span
                                          key={id}
                                          className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground"
                                        >
                                          Factura: {facturaNumber}
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const newSelectedIds = (
                                                field.value || []
                                              ).filter(
                                                (selectedId: string) =>
                                                  selectedId !== id
                                              );
                                              field.onChange(newSelectedIds);

                                              // Update selected sales requests and amounts
                                              const selected =
                                                salesRequests.filter((r) =>
                                                  newSelectedIds.includes(r.id)
                                                );
                                              setSelectedSalesRequests(
                                                selected
                                              );

                                              // Load existing payments for the selected requests
                                              if (selected.length === 1) {
                                                fetchExistingPayments(
                                                  selected[0].id
                                                );
                                              } else if (selected.length > 1) {
                                                // Fetch payments for multiple sales requests
                                                fetchExistingPaymentsForMultiple(
                                                  selected.map((r) => r.id)
                                                );
                                              } else {
                                                setExistingPayments([]);
                                                setHasMultiplePayments(false);
                                                form.setValue(
                                                  "selectedPaymentId",
                                                  undefined
                                                );
                                                form.setValue(
                                                  "selectedPaymentIds",
                                                  []
                                                );
                                              }

                                              // Recalculate amounts
                                              const availableAmount =
                                                (transaction as any)
                                                  .remaining_balance ||
                                                transaction.amount;

                                              const newAmounts: SalesRequestAmount[] =
                                                [];
                                              let remainingAmount =
                                                availableAmount;

                                              // Calculate all pending amounts first
                                              const pendingAmounts =
                                                selected.map((request) => {
                                                  const pending =
                                                    resolveGross(request) -
                                                    Math.round(
                                                      request.paid_amount || 0
                                                    );
                                                  return Math.max(0, pending);
                                                });

                                              const totalPendingAmount =
                                                pendingAmounts.reduce(
                                                  (sum, amt) => sum + amt,
                                                  0
                                                );

                                              selected.forEach(
                                                (request, index) => {
                                                  const pendingAmount =
                                                    pendingAmounts[index];
                                                  let amountToAssign = 0;

                                                  if (pendingAmount <= 0) {
                                                    amountToAssign = 0;
                                                  } else if (
                                                    index ===
                                                    selected.length - 1
                                                  ) {
                                                    // Last item gets all remaining amount
                                                    amountToAssign = Math.min(
                                                      pendingAmount,
                                                      remainingAmount
                                                    );
                                                  } else {
                                                    // Proportional distribution, but use round instead of floor to avoid precision loss
                                                    if (
                                                      totalPendingAmount > 0
                                                    ) {
                                                      const proportion =
                                                        pendingAmount /
                                                        totalPendingAmount;
                                                      const proportionalAmount =
                                                        availableAmount *
                                                        proportion;
                                                      amountToAssign = Math.min(
                                                        pendingAmount,
                                                        Math.round(
                                                          proportionalAmount
                                                        )
                                                      );
                                                    }
                                                  }

                                                  newAmounts.push({
                                                    salesRequestId: request.id,
                                                    amount: amountToAssign,
                                                  });
                                                  remainingAmount -=
                                                    amountToAssign;
                                                }
                                              );

                                              setSalesRequestAmounts(
                                                newAmounts
                                              );
                                              form.setValue(
                                                "salesRequestAmounts",
                                                newAmounts
                                              );

                                              // Update validation
                                              const newSchema =
                                                createFormSchema(
                                                  selected,
                                                  availableAmount
                                                );
                                              setCurrentSchema(newSchema);
                                              form.clearErrors();
                                              form.trigger();
                                            }}
                                            className="ml-1 h-3 w-3 rounded-full hover:bg-secondary-foreground/20"
                                          >
                                            <X className="h-2 w-2" />
                                          </button>
                                        </span>
                                      );
                                    })
                                  )}
                                </div>
                                <ChevronDown
                                  className={`h-4 w-4 opacity-50 transition-transform duration-200 ${
                                    isDropdownOpen ? "rotate-180" : ""
                                  }`}
                                />
                              </button>

                              {/* Dropdown */}
                              {isDropdownOpen && (
                                <div className="absolute z-50 mt-1 max-h-[200px] w-full origin-top overflow-y-auto rounded-md border border-input bg-background shadow-lg duration-200 ease-out animate-in fade-in-0 zoom-in-95 slide-in-from-top-2">
                                  {salesRequests
                                    .filter((request) => !request.paid)
                                    .map((request) => {
                                      const facturaDoc =
                                        request.sales_request_documents?.find(
                                          (doc) =>
                                            doc.document_type === "factura" &&
                                            doc.bsale_number
                                        );
                                      const facturaNumber =
                                        facturaDoc?.bsale_number ||
                                        `ID: ${request.id.substring(0, 8)}...`;
                                      const hasExistingPayments =
                                        (request.paid_amount || 0) > 0;
                                      const pendingAmount =
                                        resolveGross(request) -
                                        Math.round(request.paid_amount || 0);
                                      const isSelected = (
                                        field.value || []
                                      ).includes(request.id);

                                      return (
                                        <div
                                          key={request.id}
                                          onClick={() => {
                                            const currentSelected =
                                              field.value || [];
                                            const newSelectedIds = isSelected
                                              ? currentSelected.filter(
                                                  (id: string) =>
                                                    id !== request.id
                                                )
                                              : [
                                                  ...currentSelected,
                                                  request.id,
                                                ];

                                            field.onChange(newSelectedIds);

                                            // Update selected sales requests and amounts
                                            const selected =
                                              salesRequests.filter((r) =>
                                                newSelectedIds.includes(r.id)
                                              );
                                            setSelectedSalesRequests(selected);

                                            // Load existing payments for the selected requests
                                            if (selected.length === 1) {
                                              fetchExistingPayments(
                                                selected[0].id
                                              );
                                            } else if (selected.length > 1) {
                                              // Fetch payments for multiple sales requests
                                              fetchExistingPaymentsForMultiple(
                                                selected.map((r) => r.id)
                                              );
                                            } else {
                                              setExistingPayments([]);
                                              setHasMultiplePayments(false);
                                              form.setValue(
                                                "selectedPaymentId",
                                                undefined
                                              );
                                              form.setValue(
                                                "selectedPaymentIds",
                                                []
                                              );
                                            }

                                            // Calculate automatic amount distribution
                                            const availableAmount =
                                              (transaction as any)
                                                .remaining_balance ||
                                              transaction.amount;

                                            const newAmounts: SalesRequestAmount[] =
                                              [];
                                            let remainingAmount =
                                              availableAmount;
                                            let totalAssignedSoFar = 0;

                                            // Calculate all pending amounts first
                                            const pendingAmounts = selected.map(
                                              (req) => {
                                                const pending =
                                                  resolveGross(req) -
                                                  Math.round(
                                                    req.paid_amount || 0
                                                  );
                                                return Math.max(0, pending);
                                              }
                                            );

                                            const totalPendingAmount =
                                              pendingAmounts.reduce(
                                                (sum, amt) => sum + amt,
                                                0
                                              );

                                            selected.forEach((req, index) => {
                                              const pendingAmt =
                                                pendingAmounts[index];
                                              let amountToAssign = 0;

                                              if (pendingAmt <= 0) {
                                                amountToAssign = 0;
                                              } else if (
                                                index ===
                                                selected.length - 1
                                              ) {
                                                // Last item gets all remaining amount
                                                amountToAssign = Math.min(
                                                  pendingAmt,
                                                  remainingAmount
                                                );
                                              } else {
                                                // Proportional distribution using round instead of floor
                                                if (totalPendingAmount > 0) {
                                                  const proportion =
                                                    pendingAmt /
                                                    totalPendingAmount;
                                                  const proportionalAmount =
                                                    availableAmount *
                                                    proportion;
                                                  amountToAssign = Math.min(
                                                    pendingAmt,
                                                    Math.round(
                                                      proportionalAmount
                                                    )
                                                  );
                                                }
                                              }

                                              newAmounts.push({
                                                salesRequestId: req.id,
                                                amount: amountToAssign,
                                              });
                                              remainingAmount -= amountToAssign;
                                              totalAssignedSoFar +=
                                                amountToAssign;
                                            });

                                            setSalesRequestAmounts(newAmounts);
                                            form.setValue(
                                              "salesRequestAmounts",
                                              newAmounts
                                            );

                                            // Update schema for validation
                                            const newSchema = createFormSchema(
                                              selected,
                                              availableAmount
                                            );
                                            setCurrentSchema(newSchema);

                                            // Clear errors and re-validate
                                            form.clearErrors();
                                            form.trigger();
                                          }}
                                          className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-accent ${
                                            isSelected ? "bg-accent" : ""
                                          }`}
                                        >
                                          <div
                                            className={`flex h-4 w-4 items-center justify-center rounded border ${
                                              isSelected
                                                ? "border-primary bg-primary"
                                                : "border-input"
                                            }`}
                                          >
                                            {isSelected && (
                                              <CheckIcon className="h-3 w-3 text-primary-foreground" />
                                            )}
                                          </div>
                                          <div className="flex-1 truncate">
                                            <div className="font-medium">
                                              Factura: {facturaNumber}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                              {request.created_at_formatted
                                                ? `${request.created_at_formatted} | `
                                                : ""}
                                              Estado:{" "}
                                              {getStatusDisplayName(
                                                request.status
                                              )}
                                              {hasExistingPayments &&
                                              !request.paid
                                                ? " [Con Pagos]"
                                                : ""}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                              Total: $
                                              {formatCurrency(
                                                resolveGross(request)
                                              )}{" "}
                                              | Pagado: $
                                              {formatCurrency(
                                                request.paid_amount || 0
                                              )}{" "}
                                              | Pendiente: $
                                              {formatCurrency(pendingAmount)}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}

                                  {salesRequests.filter(
                                    (request) => !request.paid
                                  ).length === 0 && (
                                    <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                                      No hay solicitudes pendientes para este
                                      cliente
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </FormControl>
                        <FormDescription className="text-xs text-muted-foreground">
                          Seleccione las solicitudes de venta a las que se
                          aplicará este pago.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Operation Type Selection - only show after sales request is selected */}
                {assignmentType === "sales_request" &&
                  selectedSalesRequests.length > 0 &&
                  showOperationSelection && (
                    <div className="space-y-3 rounded-lg border p-4">
                      <h4 className="text-sm font-semibold">
                        Tipo de Operación
                      </h4>
                      <FormField
                        control={form.control}
                        name="operationType"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-col space-y-3"
                              >
                                <div className="flex items-center space-x-3">
                                  <RadioGroupItem
                                    value="create_new"
                                    id="create_new"
                                  />
                                  <div className="grid gap-1.5 leading-none">
                                    <Label
                                      htmlFor="create_new"
                                      className="flex items-center text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                      Registrar nuevo pago
                                    </Label>
                                  </div>
                                </div>
                                {existingPayments.filter(
                                  (p) => !p.rail_movement_id
                                ).length > 0 && (
                                  <div className="flex items-start space-x-3">
                                    <RadioGroupItem
                                      value="assign_existing"
                                      id="assign_existing"
                                      className="mt-1"
                                    />
                                    <div className="grid gap-1.5 leading-none">
                                      <Label
                                        htmlFor="assign_existing"
                                        className="flex items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                      >
                                        Asignar pago existente
                                      </Label>
                                      <p className="text-xs text-muted-foreground">
                                        Vincular un pago ya registrado a la
                                        transferencia (
                                        {
                                          existingPayments.filter(
                                            (p) => !p.rail_movement_id
                                          ).length
                                        }{" "}
                                        pago
                                        {existingPayments.filter(
                                          (p) => !p.rail_movement_id
                                        ).length > 1
                                          ? "s"
                                          : ""}{" "}
                                        disponible
                                        {existingPayments.filter(
                                          (p) => !p.rail_movement_id
                                        ).length > 1
                                          ? "s"
                                          : ""}
                                        )
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                {/* Show selected sales requests with individual amount inputs - only for create_new */}
                {assignmentType === "sales_request" &&
                  selectedSalesRequests.length > 0 &&
                  operationType === "create_new" && (
                    <div className="space-y-4 rounded-lg border p-4">
                      <h4 className="text-sm font-semibold">
                        Distribución de Montos
                      </h4>
                      <div className="space-y-3">
                        {selectedSalesRequests.map((request) => {
                          const requestAmount =
                            salesRequestAmounts.find(
                              (a) => a.salesRequestId === request.id
                            )?.amount || 0;
                          const pendingAmount =
                            resolveGross(request) - (request.paid_amount || 0);
                          const facturaDoc =
                            request.sales_request_documents?.find(
                              (doc) =>
                                doc.document_type === "factura" &&
                                doc.bsale_number
                            );
                          const facturaNumber =
                            facturaDoc?.bsale_number ||
                            `ID: ${request.id.substring(0, 8)}...`;

                          return (
                            <div
                              key={request.id}
                              className="grid gap-2 rounded border p-3"
                            >
                              <div className="text-sm font-medium">
                                Factura: {facturaNumber}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                                <div>
                                  Total: $
                                  {formatCurrency(resolveGross(request))}
                                </div>
                                <div>
                                  Pagado: $
                                  {formatCurrency(request.paid_amount || 0)}
                                </div>
                                <div>
                                  Pendiente: ${formatCurrency(pendingAmount)}
                                </div>
                                <div>
                                  Estado: {getStatusDisplayName(request.status)}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Label
                                  htmlFor={`amount-${request.id}`}
                                  className="text-sm"
                                >
                                  Monto a asignar:
                                </Label>
                                <Input
                                  id={`amount-${request.id}`}
                                  type="number"
                                  step="0.01"
                                  min={pendingAmount <= 0 ? "0" : "0.01"}
                                  max={Math.min(
                                    pendingAmount > 0 ? pendingAmount : 0,
                                    (transaction as any).remaining_balance ||
                                      transaction.amount
                                  )}
                                  value={Math.round(requestAmount)}
                                  onChange={(e) => {
                                    const newAmount =
                                      parseFloat(e.target.value) || 0;
                                    const updatedAmounts =
                                      salesRequestAmounts.map((a) =>
                                        a.salesRequestId === request.id
                                          ? { ...a, amount: newAmount }
                                          : a
                                      );
                                    setSalesRequestAmounts(updatedAmounts);
                                    form.setValue(
                                      "salesRequestAmounts",
                                      updatedAmounts
                                    );
                                    form.trigger();
                                  }}
                                  className="w-32"
                                  placeholder={
                                    pendingAmount <= 0 ? "0" : "Monto"
                                  }
                                />
                                <span className="text-xs text-muted-foreground">
                                  {pendingAmount <= 0
                                    ? "(Solo marcar como pagado)"
                                    : `Máx: $${formatCurrency(Math.min(pendingAmount, (transaction as any).remaining_balance || transaction.amount))}`}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Summary */}
                      {(() => {
                        const totalToAssign = salesRequestAmounts.reduce(
                          (sum, a) => sum + a.amount,
                          0
                        );
                        const available =
                          (transaction as any).remaining_balance ||
                          transaction.amount;
                        const remaining = available - totalToAssign;

                        return (
                          <div className="grid grid-cols-3 gap-3 border-t pt-3">
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-muted-foreground">
                                Total a asignar
                              </p>
                              <p className="text-sm font-semibold tabular-nums">
                                ${formatCurrency(totalToAssign)}
                              </p>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-muted-foreground">
                                Disponible
                              </p>
                              <p className="text-sm font-semibold tabular-nums">
                                ${formatCurrency(available)}
                              </p>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-muted-foreground">
                                Restante
                              </p>
                              <p
                                className={`text-sm font-semibold tabular-nums ${
                                  remaining < 0 ? "text-red-700" : ""
                                }`}
                              >
                                ${formatCurrency(remaining)}
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                {/* Show existing payments selection for assign_existing operation */}
                {assignmentType === "sales_request" &&
                  selectedSalesRequests.length >= 1 &&
                  operationType === "assign_existing" &&
                  existingPayments.length > 0 && (
                    <div className="space-y-4 rounded-lg border p-4">
                      <h4 className="text-sm font-semibold">
                        Seleccionar Pago Existente
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {selectedSalesRequests.length > 1
                          ? "Las solicitudes seleccionadas tienen pagos registrados. Seleccione los pagos que deben ser vinculados al movimiento de Fintoc."
                          : hasMultiplePayments
                            ? "Esta solicitud de venta tiene múltiples pagos registrados. Seleccione cuál pago debe ser vinculado al movimiento de Fintoc."
                            : "Seleccione el pago que debe ser vinculado al movimiento de Fintoc."}
                      </p>

                      {/* Single payment selection (radio buttons) */}
                      {selectedSalesRequests.length === 1 && (
                        <FormField
                          control={form.control}
                          name="selectedPaymentId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Seleccionar Pago a Vincular</FormLabel>
                              <FormControl>
                                <div className="space-y-2">
                                  {existingPayments
                                    .filter(
                                      (payment) => !payment.rail_movement_id
                                    )
                                    .map((payment) => (
                                      <div
                                        key={payment.id}
                                        onClick={() =>
                                          field.onChange(payment.id)
                                        }
                                        className={`cursor-pointer rounded-md border p-3 transition-colors ${
                                          field.value === payment.id
                                            ? "border-primary bg-primary/5"
                                            : "border-input hover:bg-muted/40"
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <div
                                            className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                              field.value === payment.id
                                                ? "border-primary bg-primary"
                                                : "border-input"
                                            }`}
                                          >
                                            {field.value === payment.id && (
                                              <CheckIcon className="h-2.5 w-2.5 text-primary-foreground" />
                                            )}
                                          </div>
                                          <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                              <span className="font-medium tabular-nums">
                                                $
                                                {formatCurrency(payment.amount)}
                                              </span>
                                              <span className="text-sm text-muted-foreground">
                                                {payment.payment_method}
                                              </span>
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                              {payment.bank &&
                                                `${payment.bank} • `}
                                              {new Date(
                                                payment.created_at
                                              ).toLocaleDateString("es-ES", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              })}
                                            </div>
                                            {payment.amount ===
                                              transaction.amount && (
                                              <div className="mt-1 flex items-center gap-1 text-xs font-medium text-green-700">
                                                <CheckIcon className="h-3 w-3" />
                                                Monto coincide exactamente
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </FormControl>
                              <FormDescription>
                                Seleccione el pago que corresponde al movimiento
                                de Fintoc por $
                                {formatCurrency(transaction.amount)}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {/* Multiple payment selection (checkboxes) */}
                      {selectedSalesRequests.length > 1 && (
                        <FormField
                          control={form.control}
                          name="selectedPaymentIds"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                Seleccionar Pagos a Vincular
                              </FormLabel>
                              <FormControl>
                                <div className="space-y-2">
                                  {existingPayments
                                    .filter(
                                      (payment) => !payment.rail_movement_id
                                    )
                                    .map((payment) => {
                                      const isSelected = (
                                        field.value || []
                                      ).includes(payment.id);
                                      return (
                                        <div
                                          key={payment.id}
                                          onClick={() => {
                                            const currentSelected =
                                              field.value || [];
                                            const newSelected = isSelected
                                              ? currentSelected.filter(
                                                  (id: number) =>
                                                    id !== payment.id
                                                )
                                              : [
                                                  ...currentSelected,
                                                  payment.id,
                                                ];
                                            field.onChange(newSelected);
                                          }}
                                          className={`cursor-pointer rounded-md border p-3 transition-colors ${
                                            isSelected
                                              ? "border-primary bg-primary/5"
                                              : "border-input hover:bg-muted/40"
                                          }`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <div
                                              className={`flex h-4 w-4 items-center justify-center rounded border ${
                                                isSelected
                                                  ? "border-primary bg-primary"
                                                  : "border-input"
                                              }`}
                                            >
                                              {isSelected && (
                                                <CheckIcon className="h-2.5 w-2.5 text-primary-foreground" />
                                              )}
                                            </div>
                                            <div className="flex-1">
                                              <div className="flex items-center justify-between">
                                                <span className="font-medium tabular-nums">
                                                  $
                                                  {formatCurrency(
                                                    payment.amount
                                                  )}
                                                </span>
                                                <span className="text-sm text-muted-foreground">
                                                  {payment.payment_method}
                                                </span>
                                              </div>
                                              <div className="text-sm text-muted-foreground">
                                                {payment.sales_request && (
                                                  <>
                                                    <span className="font-medium text-foreground">
                                                      {(() => {
                                                        const request =
                                                          selectedSalesRequests.find(
                                                            (r) =>
                                                              r.id ===
                                                              payment.sales_request
                                                          );
                                                        const facturaDoc =
                                                          request?.sales_request_documents?.find(
                                                            (doc) =>
                                                              doc.document_type ===
                                                                "factura" &&
                                                              doc.bsale_number
                                                          );
                                                        return (
                                                          facturaDoc?.bsale_number ||
                                                          `ID: ${payment.sales_request.substring(0, 8)}...`
                                                        );
                                                      })()}
                                                    </span>
                                                    {" • "}
                                                  </>
                                                )}
                                                {payment.bank &&
                                                  `${payment.bank} • `}
                                                {new Date(
                                                  payment.created_at
                                                ).toLocaleDateString("es-ES", {
                                                  day: "2-digit",
                                                  month: "2-digit",
                                                  year: "numeric",
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                })}
                                              </div>
                                              {payment.amount ===
                                                transaction.amount && (
                                                <div className="mt-1 flex items-center gap-1 text-xs font-medium text-green-700">
                                                  <CheckIcon className="h-3 w-3" />
                                                  Monto coincide exactamente
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </FormControl>
                              <FormDescription>
                                Seleccione los pagos que corresponden al
                                movimiento de Fintoc por $
                                {formatCurrency(transaction.amount)}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {/* Running total for multiple payments */}
                      {selectedSalesRequests.length > 1 &&
                        selectedPaymentIds &&
                        selectedPaymentIds.length > 0 && (
                          <div className="rounded-md border bg-muted/40 p-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium">
                                Total de pagos seleccionados:
                              </span>
                              <span className="font-semibold tabular-nums">
                                $
                                {formatCurrency(
                                  existingPayments
                                    .filter((p) =>
                                      selectedPaymentIds.includes(p.id)
                                    )
                                    .reduce((sum, p) => sum + p.amount, 0)
                                )}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">
                                Total transferencias:
                              </span>
                              <span className="font-medium tabular-nums">
                                ${formatCurrency(transaction.amount)}
                              </span>
                            </div>
                            {(() => {
                              const selectedTotal = existingPayments
                                .filter((p) =>
                                  selectedPaymentIds.includes(p.id)
                                )
                                .reduce((sum, p) => sum + p.amount, 0);
                              const difference =
                                selectedTotal - transaction.amount;

                              if (difference > 0) {
                                return (
                                  <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-700">
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    <span>
                                      Total excede el movimiento por $
                                      {formatCurrency(difference)}
                                    </span>
                                  </div>
                                );
                              } else if (difference < 0) {
                                return (
                                  <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-700">
                                    <Info className="h-3.5 w-3.5" />
                                    <span>
                                      Restante: $
                                      {formatCurrency(Math.abs(difference))}
                                    </span>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-green-700">
                                    <CheckIcon className="h-3.5 w-3.5" />
                                    <span>Total coincide exactamente</span>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        )}
                    </div>
                  )}

                {assignmentType === "manual_factura" && (
                  <FormField
                    control={form.control}
                    name="manualFacturaId"
                    render={({ field }) => (
                      <FormItem className="relative z-20 w-full min-w-0">
                        <FormLabel>Factura Manual</FormLabel>
                        <FormControl className="relative z-20">
                          <Select
                            disabled={
                              !selectedClientId ||
                              loadingManualFacturas ||
                              loading
                            }
                            onValueChange={(value) => {
                              field.onChange(value);
                              // Auto-populate amount based on selected manual factura
                              const selectedFactura = manualFacturas.find(
                                (f) => f.id === value
                              );
                              if (selectedFactura) {
                                form.setValue(
                                  "amount",
                                  Math.min(
                                    selectedFactura.balance || 0,
                                    (transaction as any).remaining_balance ||
                                      transaction.amount
                                  )
                                );
                                // Trigger form validation to enable the submit button
                                form.trigger();
                              }
                            }}
                            value={field.value}
                          >
                            <SelectTrigger className="relative z-10 w-full text-ellipsis">
                              <SelectValue placeholder="Seleccionar factura manual" />
                            </SelectTrigger>
                            <SelectContent
                              className="z-[9999] w-[var(--radix-select-trigger-width)]"
                              position="popper"
                              align="start"
                              sideOffset={5}
                            >
                              {loadingManualFacturas ? (
                                <div className="flex items-center justify-center p-2">
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  <span>Cargando...</span>
                                </div>
                              ) : manualFacturas.length === 0 ? (
                                <div className="p-2 text-center text-sm text-muted-foreground">
                                  No hay facturas manuales pendientes para este
                                  cliente
                                </div>
                              ) : (
                                manualFacturas.map((factura) => (
                                  <SelectItem
                                    key={factura.id}
                                    value={factura.id}
                                    className="truncate"
                                  >
                                    <div className="truncate">
                                      {factura.doc_number
                                        ? factura.doc_number
                                        : `ID: ${factura.id.substring(0, 8)}...`}{" "}
                                      |{" "}
                                      {getDocTypeDisplayName(factura.doc_type)}{" "}
                                      | Saldo: $
                                      {formatCurrency(factura.balance || 0)} |
                                      Vence:{" "}
                                      {factura.payment_due_date
                                        ? new Date(
                                            factura.payment_due_date
                                          ).toLocaleDateString("es-ES")
                                        : "Sin fecha"}
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormDescription className="truncate">
                          Seleccione la factura manual a la que se aplicará este
                          pago
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Amount field for manual facturas only */}
                {assignmentType === "manual_factura" && (
                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Monto a Asignar *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={
                              (transaction as any).remaining_balance ||
                              transaction.amount
                            }
                            placeholder="Ingrese el monto"
                            {...field}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          Máximo disponible: $
                          {formatCurrency(
                            (transaction as any).remaining_balance ||
                              transaction.amount
                          )}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </form>
            </Form>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {linkedPaymentInfo ? "Cerrar" : "Cancelar"}
          </Button>

          {linkedPaymentInfo ? (
            <Button
              type="button"
              variant="destructive"
              onClick={handleUnlink}
              disabled={unlinkLoading}
            >
              {unlinkLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Desvinculando...
                </>
              ) : (
                <>
                  <Unlink className="mr-2 h-4 w-4" />
                  Desvincular Pago
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={
                loading ||
                (assignmentType === "sales_request" &&
                  (!salesRequestIds || salesRequestIds.length === 0)) ||
                (assignmentType === "sales_request" &&
                  operationType === "assign_existing" &&
                  ((salesRequestIds?.length === 1 && !selectedPaymentId) ||
                    (salesRequestIds?.length > 1 &&
                      (!selectedPaymentIds ||
                        selectedPaymentIds.length === 0)))) ||
                (assignmentType === "manual_factura" && !manualFacturaId)
              }
              onClick={() => {
                // Force submission even if form thinks it's invalid
                const formData = form.getValues();
                try {
                  onSubmit(formData as any);
                } catch (error) {
                  console.error("Error calling onSubmit:", error);
                }
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Asignando...
                </>
              ) : (
                <>
                  {operationType === "create_new" ? (
                    <>
                      <CheckIcon className="mr-2 h-4 w-4" />
                      Crear y Asignar Pago
                    </>
                  ) : (
                    <>
                      <Link2 className="mr-2 h-4 w-4" />
                      Vincular Pago Existente
                    </>
                  )}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
